/* eslint-disable no-restricted-globals */
import {
  type BrowserClient,
  type BrowserConfig,
  type EnrichmentPlugin,
  type ElementInteractionsOptions,
  DEFAULT_EXPOSURE_DURATION,
  DEFAULT_CSS_SELECTOR_ALLOWLIST,
  DEFAULT_ACTION_CLICK_ALLOWLIST,
  DEFAULT_DATA_ATTRIBUTE_PREFIX,
  IDiagnosticsClient,
  getGlobalScope,
  getSdkInitializationOffset,
  getOrCreateWindowMessenger,
  enableBackgroundCapture,
  multicast,
} from '@amplitude/analytics-core';
import { VERSION } from './version';
import * as constants from './constants';
import {
  createShouldTrackEvent,
  type ElementBasedTimestampedEvent,
  type TimestampedEvent,
  type NavigateEvent,
  getNormalizedPageUrl,
  normalizePageUrl,
  resolveHistoryNavigationUrl,
} from './helpers';
import { enableVisualTagging } from './libs/messenger';
import { trackClicks } from './autocapture/track-click';
import { trackChange } from './autocapture/track-change';
import { trackActionClick } from './autocapture/track-action-click';
import { trackScroll } from './autocapture/track-scroll';

import {
  createClickObservable,
  createScrollObservable,
  createExposureObservable,
  createMutationObservable,
} from './observables';

import {
  createLabeledEventToTriggerMap,
  createTriggerEvaluator,
  groupLabeledEventIdsByEventType,
} from './pageActions/triggers';
import { DataExtractor } from './data-extractor';
import { subscribeToElementSelectorConfig } from './element-selector-config';
import { Observable, Unsubscribable } from '@amplitude/analytics-core';
import { trackExposure } from './autocapture/track-exposure';
import { fireViewportContentUpdated, onExposure, ExposureTracker } from './autocapture/track-viewport-content-updated';
import { createInitialExposureSnapshotScheduler } from './autocapture/schedule-initial-exposure-snapshot';
import { DEFAULT_MAX_SHADOW_DOM_DEPTH } from '@amplitude/element-selector';

type NavigationType = {
  addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
};

declare global {
  interface Window {
    navigation: NavigationType;
  }
}

type BrowserEnrichmentPlugin = EnrichmentPlugin<BrowserClient, BrowserConfig>;

export type AutoCaptureOptionsWithDefaults = Required<
  Pick<ElementInteractionsOptions, 'debounceTime' | 'cssSelectorAllowlist' | 'actionClickAllowlist'>
> &
  ElementInteractionsOptions;

export enum ObservablesEnum {
  ClickObservable = 'clickObservable',
  PointerDownObservable = 'pointerDownObservable',
  ChangeObservable = 'changeObservable',
  NavigateObservable = 'navigateObservable',
  MutationObservable = 'mutationObservable',
  ScrollObservable = 'scrollObservable',
  ExposureObservable = 'exposureObservable',
  BrowserErrorObservable = 'browserErrorObservable',
  SelectionObservable = 'selectionObservable',
  MouseMoveObservable = 'mouseMoveObservable',
}

export interface AllWindowObservables {
  [ObservablesEnum.ChangeObservable]: Observable<ElementBasedTimestampedEvent<Event>>;
  // [ObservablesEnum.ErrorObservable]: Observable<TimestampedEvent<ErrorEvent>>;
  [ObservablesEnum.ClickObservable]: Observable<ElementBasedTimestampedEvent<MouseEvent>>;
  [ObservablesEnum.MutationObservable]: Observable<TimestampedEvent<MutationRecord[]>>;
  [ObservablesEnum.NavigateObservable]?: Observable<TimestampedEvent<NavigateEvent>>;
  [ObservablesEnum.ScrollObservable]: Observable<Event>; // TODO: add type for scroll event
  [ObservablesEnum.ExposureObservable]: Observable<Event>;
  [ObservablesEnum.SelectionObservable]?: Observable<void>;
}

export const autocapturePlugin = (
  options: ElementInteractionsOptions = {},
  context?: { diagnosticsClient: IDiagnosticsClient },
): BrowserEnrichmentPlugin => {
  // Set the plugin version tag
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  context?.diagnosticsClient.setTag('plugin.autocapture.version', VERSION);

  const {
    dataAttributePrefix = DEFAULT_DATA_ATTRIBUTE_PREFIX,
    visualTaggingOptions = {
      enabled: true,
    },
  } = options;

  options.cssSelectorAllowlist = options.cssSelectorAllowlist ?? DEFAULT_CSS_SELECTOR_ALLOWLIST;
  options.actionClickAllowlist = options.actionClickAllowlist ?? DEFAULT_ACTION_CLICK_ALLOWLIST;
  options.debounceTime = options.debounceTime ?? 0;
  const isViewportContentUpdatedEnabled = options.viewportContentUpdated?.enabled !== false;
  const resolvedExposureDuration =
    options.viewportContentUpdated?.exposureDuration ?? options.exposureDuration ?? DEFAULT_EXPOSURE_DURATION;
  options.viewportContentUpdated = {
    ...options.viewportContentUpdated,
    exposureDuration: resolvedExposureDuration,
  };

  options.pageUrlExcludelist = options.pageUrlExcludelist?.reduce(
    (acc: (string | RegExp | { pattern: string })[], excludePattern) => {
      if (typeof excludePattern === 'string') {
        acc.push(excludePattern);
      }
      if (excludePattern instanceof RegExp) {
        acc.push(excludePattern);
      }
      if (typeof excludePattern === 'object' && excludePattern !== null && 'pattern' in excludePattern) {
        try {
          acc.push(new RegExp(excludePattern.pattern));
        } catch (regexError) {
          console.warn(`Invalid regex pattern: ${excludePattern.pattern}`, regexError);
          return acc;
        }
      }
      return acc;
    },
    [],
  );

  const name = constants.PLUGIN_NAME;
  const type = 'enrichment';

  const subscriptions: Unsubscribable[] = [];

  const dataExtractor = new DataExtractor(options, context);

  // Page-level state shared across trackers, emitted in a single Page View End event on beforeunload
  // elementExposedForPage holds the total set of elements seen during the entire page view lifetime
  const elementExposedForPage = new Set<string>();
  // elementExposedInSentEvents holds elements already included in a flushed Viewport Content Updated event
  const elementExposedInSentEvents = new Set<string>();
  // currentElementExposed only holds the set of elements that will be flushed during the next [Amplitude] Viewport Content Updated event
  const currentElementExposed = new Set<string>();

  let beforeUnloadCleanup: () => void;
  let rescanElementsForExposure: (() => void) | undefined;

  const createObservables = (): AllWindowObservables => {
    const clickObservable = multicast(
      createClickObservable().map(
        (click) =>
          dataExtractor.addAdditionalEventProperties(
            click,
            'click',
            (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist,
            dataAttributePrefix,
          ) as ElementBasedTimestampedEvent<MouseEvent>,
      ),
    );

    const changeObservable = multicast(
      new Observable<ElementBasedTimestampedEvent<Event>>((observer) => {
        const handler = (changeEvent: Event) => {
          const enrichedChangeEvent = dataExtractor.addAdditionalEventProperties(
            changeEvent,
            'change',
            (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist,
            dataAttributePrefix,
          ) as ElementBasedTimestampedEvent<Event>;
          observer.next(enrichedChangeEvent);
        };
        /* istanbul ignore next */
        getGlobalScope()?.document.addEventListener('change', handler, { capture: true });
        /* istanbul ignore next */
        return () => getGlobalScope()?.document.removeEventListener('change', handler);
      }),
    );

    // Create observable for URL changes
    let navigateObservable: Observable<TimestampedEvent<NavigateEvent>> | undefined;

    /* istanbul ignore next */
    if (window.navigation) {
      navigateObservable = multicast(
        new Observable<TimestampedEvent<NavigateEvent>>((observer) => {
          const handler = (navigateEvent: NavigateEvent) => {
            const enrichedNavigateEvent = dataExtractor.addAdditionalEventProperties(
              navigateEvent,
              'navigate',
              (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist,
              dataAttributePrefix,
            );
            observer.next(enrichedNavigateEvent);
          };
          window.navigation.addEventListener('navigate', handler as EventListener);
          return () => {
            window.navigation.removeEventListener('navigate', handler as EventListener);
          };
        }),
      );
    }

    // Page-scoped shadow-DOM gate, shared with the frustration plugin. The
    // observables read it per callback and subscribe to its arming to run their
    // shadow discovery scans; see `shadow-mode.ts`.
    const shadowGate = dataExtractor.shadowGate;

    const mutationObservable = multicast(
      createMutationObservable(shadowGate).map((mutation) =>
        dataExtractor.addAdditionalEventProperties(
          mutation,
          'mutation',
          (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist,
          dataAttributePrefix,
        ),
      ),
    );

    const scrollObservable = createScrollObservable();

    const exposureObservable = createExposureObservable(
      mutationObservable,
      (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist,
      (rescan) => {
        rescanElementsForExposure = rescan;
      },
      shadowGate,
    );

    return {
      [ObservablesEnum.ChangeObservable]: changeObservable,
      // [ObservablesEnum.ErrorObservable]: errorObservable,
      [ObservablesEnum.ClickObservable]: clickObservable,
      [ObservablesEnum.MutationObservable]: mutationObservable,
      [ObservablesEnum.NavigateObservable]: navigateObservable,
      [ObservablesEnum.ScrollObservable]: scrollObservable,
      [ObservablesEnum.ExposureObservable]: exposureObservable,
    };
  };

  // Group labeled events by event type (eg. click, change)
  let groupedLabeledEvents = groupLabeledEventIdsByEventType(Object.values(options.pageActions?.labeledEvents ?? {}));

  let labeledEventToTriggerMap = createLabeledEventToTriggerMap(options.pageActions?.triggers ?? []);

  // Evaluate triggers for the given event by running the actions associated with the matching triggers
  const evaluateTriggers = createTriggerEvaluator(
    groupedLabeledEvents,
    labeledEventToTriggerMap,
    dataExtractor,
    options,
  );

  // Function to recalculate internal variables when remote config is updated
  const recomputePageActionsData = (remotePageActions: ElementInteractionsOptions['pageActions']) => {
    if (remotePageActions) {
      // Merge remote config with local options
      options.pageActions = {
        ...options.pageActions,
        ...remotePageActions,
      };

      // Recalculate internal variables
      groupedLabeledEvents = groupLabeledEventIdsByEventType(Object.values(options.pageActions.labeledEvents ?? {}));
      labeledEventToTriggerMap = createLabeledEventToTriggerMap(options.pageActions.triggers ?? []);

      // Update evaluateTriggers function
      evaluateTriggers.update(groupedLabeledEvents, labeledEventToTriggerMap, options);
    }
  };

  const setup: BrowserEnrichmentPlugin['setup'] = async (config, amplitude) => {
    /* istanbul ignore if */
    if (typeof document === 'undefined') {
      return;
    }

    dataExtractor.setPageViewApiKey(config.apiKey);
    // Sample at install time so a later viewport event still reports when the SDK started.
    getSdkInitializationOffset(config.apiKey);

    let pageViewEndFired = false;

    // Fetch remote config for pageActions in a non-blocking manner
    if (config.fetchRemoteConfig) {
      if (!config.remoteConfigClient) {
        // TODO(xinyi): Diagnostics.recordEvent
        config.loggerProvider.debug('Remote config client is not provided, skipping remote config fetch');
      } else {
        config.remoteConfigClient.subscribe('configs.analyticsSDK.pageActions', 'all', (remoteConfig) => {
          recomputePageActionsData(remoteConfig as ElementInteractionsOptions['pageActions']);
        });
      }
    }
    if (options.shadowDomEnabled === true) {
      let maxShadowDomDepth = options.maxShadowDomDepth;
      if (typeof maxShadowDomDepth !== 'number') {
        maxShadowDomDepth = DEFAULT_MAX_SHADOW_DOM_DEPTH;
      }
      dataExtractor.updateSelectorConfig({ shadowDomEnabled: true, maxShadowDomDepth });
    }

    // Keep the data extractor's selector engine in sync with element-selector
    // remote config. Dormant by default; flips selector output for every
    // selector-bearing autocapture event when an org enables the engine.
    const elementSelectorConfigCleanup = subscribeToElementSelectorConfig(config, dataExtractor);
    if (elementSelectorConfigCleanup) {
      subscriptions.push({ unsubscribe: elementSelectorConfigCleanup });
    }

    // Create should track event functions the different allowlists
    const shouldTrackEvent = createShouldTrackEvent(
      options,
      (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist,
    );
    const shouldTrackActionClick = createShouldTrackEvent(
      options,
      (options as AutoCaptureOptionsWithDefaults).actionClickAllowlist,
    );

    // Create observables for events on the window
    const allObservables = createObservables();

    // Create subscriptions
    const clickTrackingSubscription = trackClicks({
      allObservables,
      amplitude,
      shouldTrackEvent: shouldTrackEvent,
      evaluateTriggers: evaluateTriggers.evaluate.bind(evaluateTriggers),
    });

    subscriptions.push(clickTrackingSubscription);

    const changeSubscription = trackChange({
      allObservables,
      getEventProperties: (...args) => dataExtractor.getEventProperties(...args, dataAttributePrefix),
      amplitude,
      shouldTrackEvent: shouldTrackEvent,
      evaluateTriggers: evaluateTriggers.evaluate.bind(evaluateTriggers),
    });
    subscriptions.push(changeSubscription);

    const actionClickSubscription = trackActionClick({
      allObservables,
      options: options as AutoCaptureOptionsWithDefaults,
      getEventProperties: (...args) => dataExtractor.getEventProperties(...args, dataAttributePrefix),
      amplitude,
      shouldTrackEvent,
      shouldTrackActionClick: shouldTrackActionClick,
    });
    if (actionClickSubscription) {
      subscriptions.push(actionClickSubscription);
    }

    const scrollTracker = trackScroll({
      allObservables,
      amplitude,
    });
    subscriptions.push(scrollTracker);

    // Seeded at attach so an empty first snapshot is not a scroll change.
    const lastScroll: { maxX: undefined | number; maxY: undefined | number; minY: undefined | number } = {
      ...scrollTracker.getState(),
    };

    const writeScrollBaseline = () => {
      scrollTracker.seed();
      const seeded = scrollTracker.getState();
      lastScroll.maxX = seeded.maxX;
      lastScroll.maxY = seeded.maxY;
      lastScroll.minY = seeded.minY;
    };

    let awaitingNavigationBaseline = false;
    let deferredViewportFlush = false;
    let deferredPageEnd = false;
    let pendingNavigationSignal: AbortSignal | undefined;

    const trackers: { exposure?: ExposureTracker & Unsubscribable } = {};

    const globalScope = getGlobalScope();

    let initialExposureSnapshotScheduler: ReturnType<typeof createInitialExposureSnapshotScheduler> | undefined;

    /** Drops scroll and exposure samples taken during the previous navigation wait. */
    const rearmDeferredPageView = () => {
      scrollTracker.reset();
      const resetScroll = scrollTracker.getState();
      lastScroll.maxX = resetScroll.maxX;
      lastScroll.maxY = resetScroll.maxY;
      lastScroll.minY = resetScroll.minY;
      currentElementExposed.clear();
      elementExposedForPage.clear();
      elementExposedInSentEvents.clear();
      /* istanbul ignore next */
      trackers.exposure?.reset();
      /* istanbul ignore next */
      initialExposureSnapshotScheduler?.reset();
    };

    const markNavigationScrollBaseline = () => {
      awaitingNavigationBaseline = true;
    };

    const handleViewportContentUpdated = (isPageEnd: boolean) => {
      if (awaitingNavigationBaseline) {
        // Redirect page-ends must not become deferredPageEnd or the seed is reset.
        if (isPageEnd) {
          rearmDeferredPageView();
          return;
        }
        deferredViewportFlush = true;
        return;
      }
      if (isPageEnd) {
        /* istanbul ignore next */
        initialExposureSnapshotScheduler?.reset();
      }
      // A page end inside 100ms is not emitted again, but the next page view still starts fresh.
      if (isPageEnd && pageViewEndFired) {
        rearmDeferredPageView();
        return;
      }
      if (isPageEnd) {
        pageViewEndFired = true;
        setTimeout(() => {
          pageViewEndFired = false;
        }, 100);
      }

      fireViewportContentUpdated({
        amplitude,
        scrollTracker,
        currentElementExposed,
        elementExposedForPage,
        elementExposedInSentEvents,
        exposureTracker: trackers.exposure,
        isPageEnd,
        lastScroll,
        apiKey: config.apiKey,
      });
    };

    let abortPublishTimer: ReturnType<typeof setTimeout> | undefined;
    const clearAbortPublishTimer = () => {
      if (abortPublishTimer) {
        clearTimeout(abortPublishTimer);
        abortPublishTimer = undefined;
      }
    };

    // Abort fires before the next navigate event, so defer the seed until a redirect can cancel it.
    const scheduleAbortPublish = () => {
      clearAbortPublishTimer();
      abortPublishTimer = setTimeout(() => {
        abortPublishTimer = undefined;
        publishNavigationScrollBaseline();
      }, 0);
    };

    /** Samples the current offset, replacing any sample taken during the wait. */
    const publishNavigationScrollBaseline = () => {
      clearAbortPublishTimer();
      pendingNavigationSignal?.removeEventListener('abort', scheduleAbortPublish);
      pendingNavigationSignal = undefined;
      if (!awaitingNavigationBaseline) {
        return;
      }
      awaitingNavigationBaseline = false;
      scrollTracker.reset();
      writeScrollBaseline();
      const shouldFlush = deferredViewportFlush;
      const pageEnd = deferredPageEnd;
      deferredViewportFlush = false;
      deferredPageEnd = false;
      if (!shouldFlush) {
        return;
      }
      handleViewportContentUpdated(pageEnd);
    };

    const clearPendingNavigationWait = () => {
      clearAbortPublishTimer();
      pendingNavigationSignal?.removeEventListener('abort', scheduleAbortPublish);
      pendingNavigationSignal = undefined;
      deferredViewportFlush = false;
      deferredPageEnd = false;
    };

    const onNavigateError = () => {
      if (pendingNavigationSignal && !pendingNavigationSignal.aborted) {
        return;
      }
      publishNavigationScrollBaseline();
    };

    const watchNavigationSignal = (signal: AbortSignal | undefined) => {
      clearAbortPublishTimer();
      pendingNavigationSignal?.removeEventListener('abort', scheduleAbortPublish);
      pendingNavigationSignal = undefined;
      if (!signal) {
        return;
      }
      if (signal.aborted) {
        publishNavigationScrollBaseline();
        return;
      }
      pendingNavigationSignal = signal;
      signal.addEventListener('abort', scheduleAbortPublish, { once: true });
    };

    let trackedPageUrl = getNormalizedPageUrl(globalScope);

    const handleSpaNavigation = (options?: { deferScrollBaseline?: boolean }) => {
      const currentPageUrl = getNormalizedPageUrl(globalScope);
      if (currentPageUrl === trackedPageUrl) {
        return false;
      }

      if (awaitingNavigationBaseline && options && options.deferScrollBaseline) {
        clearPendingNavigationWait();
        rearmDeferredPageView();
        trackedPageUrl = currentPageUrl;
        return true;
      }

      trackedPageUrl = currentPageUrl;
      handleViewportContentUpdated(true);
      if (options?.deferScrollBaseline) {
        markNavigationScrollBaseline();
        return true;
      }
      writeScrollBaseline();
      return false;
    };

    const handleHistoryStateChange = (applyHistoryChange: () => void, nextUrl: string | URL | null | undefined) => {
      const previousPageUrl = getNormalizedPageUrl(globalScope);
      // Resolve against the pre-navigation URL. Applying history first would make
      // relative paths like `../other` resolve against the already-updated location.
      const nextPageUrl = resolveHistoryNavigationUrl(nextUrl, globalScope);
      if (previousPageUrl === nextPageUrl) {
        applyHistoryChange();
        return;
      }

      trackedPageUrl = nextPageUrl;
      // Flush the previous page before applying history so Page URL is not the destination.
      handleViewportContentUpdated(true);
      applyHistoryChange();
      writeScrollBaseline();
    };

    const handleExposure = (elementPath: string) => {
      onExposure(
        elementPath,
        elementExposedForPage,
        elementExposedInSentEvents,
        currentElementExposed,
        handleViewportContentUpdated,
      );
    };

    if (isViewportContentUpdatedEnabled) {
      trackers.exposure = trackExposure({
        allObservables,
        onExposure: handleExposure,
        dataExtractor,
        exposureDuration: resolvedExposureDuration,
      });
      if (trackers.exposure) {
        subscriptions.push(trackers.exposure);
      }

      initialExposureSnapshotScheduler = createInitialExposureSnapshotScheduler({
        mutationObservable: allObservables[ObservablesEnum.MutationObservable],
        onRescan: () => {
          /* istanbul ignore next */
          config.loggerProvider?.debug?.(
            '@amplitude/plugin-autocapture-browser: initial exposure snapshot — rescanning DOM',
          );
          rescanElementsForExposure?.();
        },
        onFlush: () => {
          /* istanbul ignore next */
          config.loggerProvider?.debug?.(
            '@amplitude/plugin-autocapture-browser: initial exposure snapshot — flushing viewport content updated',
          );
          handleViewportContentUpdated(false);
        },
        exposureDuration: resolvedExposureDuration,
      });
      initialExposureSnapshotScheduler.start();
      subscriptions.push({
        unsubscribe: () => {
          /* istanbul ignore next */
          initialExposureSnapshotScheduler?.stop();
        },
      });

      const beforeUnloadHandler = () => {
        if (awaitingNavigationBaseline) {
          deferredPageEnd = true;
          deferredViewportFlush = true;
          publishNavigationScrollBaseline();
          return;
        }
        handleViewportContentUpdated(true);
      };
      /* istanbul ignore next */
      globalScope?.addEventListener('beforeunload', beforeUnloadHandler);
      beforeUnloadCleanup = () => {
        /* istanbul ignore next */
        globalScope?.removeEventListener('beforeunload', beforeUnloadHandler);
      };
      // Ensure cleanup on teardown as well
      subscriptions.push({ unsubscribe: () => beforeUnloadCleanup() });

      // Also track on navigation (SPA)
      const navigateObservable = allObservables[ObservablesEnum.NavigateObservable];
      if (navigateObservable) {
        subscriptions.push(
          navigateObservable.subscribe((timestampedEvent) => {
            /* istanbul ignore next */
            const destinationUrl = timestampedEvent.event?.destination?.url;
            if (destinationUrl) {
              const nextPageUrl = normalizePageUrl(destinationUrl);
              if (nextPageUrl === trackedPageUrl) {
                return;
              }

              if (awaitingNavigationBaseline) {
                deferredViewportFlush = false;
                deferredPageEnd = false;
                trackedPageUrl = nextPageUrl;
                handleViewportContentUpdated(true);
                watchNavigationSignal(timestampedEvent.event.signal);
                return;
              }

              trackedPageUrl = nextPageUrl;
              handleViewportContentUpdated(true);
              markNavigationScrollBaseline();
              watchNavigationSignal(timestampedEvent.event.signal);
              return;
            }

            if (handleSpaNavigation({ deferScrollBaseline: true })) {
              watchNavigationSignal(timestampedEvent.event?.signal);
            }
          }),
        );

        const navigation = window.navigation;
        if (navigation) {
          navigation.addEventListener('navigatesuccess', publishNavigationScrollBaseline);
          navigation.addEventListener('navigateerror', onNavigateError);
          subscriptions.push({
            unsubscribe: () => {
              clearPendingNavigationWait();
              awaitingNavigationBaseline = false;
              navigation.removeEventListener('navigatesuccess', publishNavigationScrollBaseline);
              navigation.removeEventListener('navigateerror', onNavigateError);
            },
          });
        }
      } else if (globalScope) {
        const popstateHandler = () => {
          handleSpaNavigation();
        };
        /* istanbul ignore next */
        // Fallback for SPA tracking when Navigation API is not available
        globalScope.addEventListener('popstate', popstateHandler);

        /* istanbul ignore next */
        // There is no global browser listener for changes to history, so we have
        // to modify pushState directly.
        // https://stackoverflow.com/a/64927639
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const originalPushState = globalScope.history.pushState;
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const originalReplaceState = globalScope.history.replaceState;
        if (globalScope.history && originalPushState) {
          // eslint-disable-next-line @typescript-eslint/unbound-method
          globalScope.history.pushState = new Proxy(originalPushState, {
            apply: (target, thisArg, args: [unknown, string, string | URL | null | undefined]) => {
              handleHistoryStateChange(() => {
                target.apply(thisArg, args);
              }, args[2]);
            },
          });
        }
        if (globalScope.history && originalReplaceState) {
          // eslint-disable-next-line @typescript-eslint/unbound-method
          globalScope.history.replaceState = new Proxy(originalReplaceState, {
            apply: (target, thisArg, args: [unknown, string, string | URL | null | undefined]) => {
              handleHistoryStateChange(() => {
                target.apply(thisArg, args);
              }, args[2]);
            },
          });
        }

        subscriptions.push({
          unsubscribe: () => {
            /* istanbul ignore next */
            globalScope.removeEventListener('popstate', popstateHandler);
            /* istanbul ignore next */
            if (globalScope.history && originalPushState) {
              globalScope.history.pushState = originalPushState;
            }
            /* istanbul ignore next */
            if (globalScope.history && originalReplaceState) {
              globalScope.history.replaceState = originalReplaceState;
            }
          },
        });
      }
    }

    /* istanbul ignore next */
    config?.loggerProvider?.log(`${name} has been successfully added.`);

    // Setup visual tagging and background capture on the shared messenger singleton.
    // Using the singleton ensures a single message listener per page, even when
    // session-replay is also loaded.
    if (window.opener && visualTaggingOptions.enabled) {
      const allowlist = (options as AutoCaptureOptionsWithDefaults).cssSelectorAllowlist;
      const actionClickAllowlist = (options as AutoCaptureOptionsWithDefaults).actionClickAllowlist;

      const messenger = getOrCreateWindowMessenger();
      enableVisualTagging(messenger, {
        dataExtractor,
        isElementSelectable: createShouldTrackEvent(options, [...allowlist, ...actionClickAllowlist]),
        cssSelectorAllowlist: allowlist,
        actionClickAllowlist,
      });
      enableBackgroundCapture(messenger);
      /* istanbul ignore next */
      messenger.setup({
        logger: config?.loggerProvider,
        ...(config?.serverZone && { endpoint: constants.AMPLITUDE_ORIGINS_MAP[config.serverZone] }),
      });
    }
  };

  const execute: BrowserEnrichmentPlugin['execute'] = async (event) => {
    return event;
  };

  const teardown = async () => {
    for (const subscription of subscriptions) {
      subscription.unsubscribe();
    }
  };

  return {
    name,
    type,
    setup,
    execute,
    teardown,
  };
};
